import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import {
    PrimaryButton, Select, SelectOption, SelectOptionValue, Skeleton,
} from '@luminalearning/components';
import { EnvironmentKey } from 'src/Components/Pages/Developer';
import CreateTestProjectModal from 'src/Components/Developer/CreateTestProjectModal';
import EditTestProjectModal from 'src/Components/Developer/EditTestProjectModal';
import { Language, LuminaQualification } from '@luminalearning/types';
import { PencilIcon } from '@heroicons/react/solid';
import LANGUAGES from 'src/graphql/queries/languages';
import CREATE_TEST_API_PROJECT from 'src/graphql/mutations/createTestApiProject';
import UPDATE_PROJECT_PRODUCTS from 'src/graphql/mutations/updateTestApiProject';
import UPDATE_PROJECT_LANGUAGES from 'src/graphql/mutations/updateTestApiProjectLanguage';
import TEST_API_PROJECTS from 'src/graphql/queries/testApiProjects';

export type ProjectCardProject = {
    id: number;
    name: string;
    enabledProducts?: Array<{ id?: number | null; name?: string | null; colourTheme?: string | null }>;
    language1?: { id?: number | null; code?: string | null; nativeName?: string | null } | null;
    language2?: { id?: number | null; code?: string | null; nativeName?: string | null } | null;
};

type UpdateLanguageValues = {
    projectId: number;
    languageId1?: number;
    languageId2?: number;
};

type ProjectListSectionProps = {
    activeEnvironmentTab: EnvironmentKey;
    activeProjects: ProjectCardProject[];
    isLoading: boolean;
    pagination?: {
        currentPage: number;
        maxPage: number;
        previousPage: number | null;
        nextPage: number | null;
    } | null;
    onPageChange?: (page: number) => void;
    productQualifications?: LuminaQualification[] | null;
    profileId: number | null;
};

const ProjectCard = ({
    intl, project, activeEnvironmentTab, onEdit, onUpdateLanguage,
}: {
    intl: ReturnType<typeof useIntl>,
    project: ProjectCardProject,
    activeEnvironmentTab: 'test' | 'live',
    onEdit?: (project: ProjectCardProject) => void,
    onUpdateLanguage?: (values: UpdateLanguageValues) => Promise<string | undefined>,
}) => {
    const [isCopied, setIsCopied] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState<'language1' | 'language2' | null>(null);
    const [updatingLanguage, setUpdatingLanguage] = useState<'language1' | 'language2' | null>(null);
    const { data: languagesData, loading: languagesLoading } = useQuery(LANGUAGES);

    const languageOptions: SelectOptionValue[] = useMemo(
        () => {
            const langs: Language[] = languagesData?.languages ?? [];
            return langs
                .filter((lang): lang is Language & { id: number } => lang.id != null)
                .map((lang) => ({
                    id: lang.id,
                    value: `${lang.nativeName} (${lang.name})`,
                }));
        },
        [languagesData]
    );

    const projectId = String(project?.id ?? '');
    const language1Name = project?.language1?.nativeName ?? null;
    const language2Name = project?.language2?.nativeName ?? null;

    const currentLanguage1Value = useMemo(
        () => languageOptions.find((opt) => opt.id === project?.language1?.id) ?? null,
        [languageOptions, project?.language1?.id]
    );
    const currentLanguage2Value = useMemo(
        () => languageOptions.find((opt) => opt.id === project?.language2?.id) ?? null,
        [languageOptions, project?.language2?.id]
    );

    const handleLanguageSelect = async (field: 'language1' | 'language2', value: SelectOptionValue | null) => {
        if (!value || !onUpdateLanguage) return;
        const langId = Number(value.id);
        setEditingLanguage(null);
        setUpdatingLanguage(field);
        await onUpdateLanguage({
            projectId: project.id,
            ...(field === 'language1' ? { languageId1: langId } : { languageId2: langId }),
        });
        setUpdatingLanguage(null);
    };

    // Add transparency (append 1A/add 0.1 opacity) to the hex colour to use for background colour
    // This does not support shorthand hex e.g #FFF
    const toTintedBackground = (colourTheme: string | null) => {
        if (!colourTheme) {
            return undefined;
        }
        const hexMatch = /^#([0-9a-fA-F]{6})$/.test(colourTheme);
        return hexMatch ? `${colourTheme}1A` : undefined;
    };

    const products = (project?.enabledProducts ?? [])
        .map((product) => ({
            name: product?.name ?? null,
            colourTheme: product?.colourTheme ?? null,
        }))
        .filter((product): product is { name: string; colourTheme: string | null } => Boolean(product.name));
    const handleCopy = async () => {
        try {
            if (!navigator.clipboard?.writeText) {
                return;
            }
            await navigator.clipboard.writeText(projectId);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 1500);
        } catch (error) {
            setIsCopied(false);
        }
    };

    return (
        <div className='border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'>
            <div className='flex flex-col gap-y-2'>
                <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                    <span className='text-headline-reg font-bold'>{project.name}</span>
                    <span className='text-paragraph-xs text-text-subtle'>ID {projectId}</span>
                    <button
                        type='button'
                        onClick={handleCopy}
                        className={[
                            'rounded-full border border-card-normal px-3 py-1',
                            'text-paragraph-xs font-semibold text-text-subtle',
                            'transition hover:text-text-default hover:border-card-strong',
                        ].join(' ')}
                        aria-label={`Copy project ID ${projectId}`}
                    >
                        {isCopied
                            ? <FormattedMessage id="LP_DEVELOPER_COPIED" defaultMessage="Copied" />
                            : <FormattedMessage id="LP_DEVELOPER_COPY" defaultMessage="Copy" /> }
                    </button>
                </div>
                <div className='text-paragraph-xs text-text-subtle'>
                    {editingLanguage === 'language1' ? (
                        <div className='flex items-center gap-x-2'>
                            <span className='font-semibold'>
                                <FormattedMessage id="LP_DEVELOPER_PROJECT_PRIMARY_LANGUAGE" defaultMessage="Primary Language" />:
                            </span>
                            <Select
                                labelText={intl.formatMessage({ id: 'LP_DEVELOPER_PRIMARY_LANGUAGE_LABEL', defaultMessage: 'Primary Language' })}
                                placeholder={intl.formatMessage({ id: 'LP_DEVELOPER_SELECT_LANGUAGE_PLACEHOLDER', defaultMessage: 'Select a language' })}
                                value={currentLanguage1Value}
                                setValue={(val) => handleLanguageSelect('language1', val)}
                                isLoading={languagesLoading}
                            >
                                {languageOptions.map((option) => (
                                    <SelectOption key={option.id} value={option} />
                                ))}
                            </Select>
                            <button
                                type='button'
                                className='text-paragraph-xs text-text-subtle underline'
                                onClick={() => setEditingLanguage(null)}
                            >
                                <FormattedMessage id="LP_CANCEL" defaultMessage="Cancel" />
                            </button>
                        </div>
                    ) : updatingLanguage === 'language1' ? (
                        <span className='flex items-center gap-x-1'>
                            <FormattedMessage id="LP_DEVELOPER_PROJECT_PRIMARY_LANGUAGE" defaultMessage="Primary Language" />:{' '}
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={16} width={120} inline />
                        </span>
                    ) : (
                        <span className='flex items-center gap-x-1'>
                            <FormattedMessage id="LP_DEVELOPER_PROJECT_PRIMARY_LANGUAGE" defaultMessage="Primary Language" />: {language1Name ?? '—'}
                            {activeEnvironmentTab === 'test' && onUpdateLanguage && (
                                <button
                                    type='button'
                                    className='h-5 w-5 ml-1 rounded-md border'
                                    onClick={() => setEditingLanguage('language1')}
                                    aria-label={intl.formatMessage({ id: 'LP_DEVELOPER_EDIT_PRIMARY_LANGUAGE', defaultMessage: 'Edit primary language' })}
                                >
                                    <PencilIcon />
                                </button>
                            )}
                        </span>
                    )}
                </div>
                <div className='text-paragraph-xs text-text-subtle'>
                    {editingLanguage === 'language2' ? (
                        <div className='flex items-center gap-x-2'>
                            <span className='font-semibold'>
                                <FormattedMessage id="LP_DEVELOPER_PROJECT_SECONDARY_LANGUAGE" defaultMessage="Secondary Language" />:
                            </span>
                            <Select
                                labelText={intl.formatMessage({ id: 'LP_DEVELOPER_SECONDARY_LANGUAGE_LABEL', defaultMessage: 'Secondary Language' })}
                                placeholder={intl.formatMessage({ id: 'LP_DEVELOPER_SELECT_LANGUAGE_PLACEHOLDER', defaultMessage: 'Select a language' })}
                                value={currentLanguage2Value}
                                setValue={(val) => handleLanguageSelect('language2', val)}
                                isLoading={languagesLoading}
                            >
                                {languageOptions.map((option) => (
                                    <SelectOption key={option.id} value={option} />
                                ))}
                            </Select>
                            <button
                                type='button'
                                className='text-paragraph-xs text-text-subtle underline'
                                onClick={() => setEditingLanguage(null)}
                            >
                                <FormattedMessage id="LP_CANCEL" defaultMessage="Cancel" />
                            </button>
                        </div>
                    ) : updatingLanguage === 'language2' ? (
                        <span className='flex items-center gap-x-1'>
                            <FormattedMessage id="LP_DEVELOPER_PROJECT_SECONDARY_LANGUAGE" defaultMessage="Secondary Language" />:{' '}
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={16} width={120} inline />
                        </span>
                    ) : (
                        <span className='flex items-center gap-x-1'>
                            <FormattedMessage id="LP_DEVELOPER_PROJECT_SECONDARY_LANGUAGE" defaultMessage="Secondary Language" />: {language2Name ?? '—'}
                            {activeEnvironmentTab === 'test' && onUpdateLanguage && (
                                <button
                                    type='button'
                                    className='h-5 w-5 ml-1 rounded-md border'
                                    onClick={() => setEditingLanguage('language2')}
                                    aria-label={intl.formatMessage({ id: 'LP_DEVELOPER_EDIT_SECONDARY_LANGUAGE', defaultMessage: 'Edit secondary language' })}
                                >
                                    <PencilIcon />
                                </button>
                            )}
                        </span>
                    )}
                </div>
                <div className='text-paragraph-reg text-text-subtle'>
                    <div className='text-paragraph-xs font-semibold text-text-subtle flex'>
                        <FormattedMessage id="LP_DEVELOPER_PROJECT_ENABLED_PRODUCTS" defaultMessage="Enabled Products" />
                        { activeEnvironmentTab === 'test'
                            && <button
                                className='h-6 w-6 ml-3 rounded-md border-2'
                                onClick={() => onEdit?.(project)}
                                aria-label={intl.formatMessage({
                                    id: 'LS_PROJECT_LIST_EDIT_BUTTON',
                                    defaultMessage: 'Edit',
                                })}
                            >
                                <PencilIcon />
                            </button> }
                    </div>
                    <div className='mt-2 flex flex-wrap gap-2'>
                        {products.length > 0 ? (
                            products.map((product) => (
                                <span
                                    key={product.name}
                                    className={[
                                        'inline-flex items-center rounded-full border',
                                        'border-card-normal bg-white px-2 py-1',
                                        'text-paragraph-xs font-semibold text-text-subtle',
                                    ].join(' ')}
                                    style={product.colourTheme ? {
                                        color: product.colourTheme,
                                        borderColor: product.colourTheme,
                                        backgroundColor: toTintedBackground(product.colourTheme),
                                    } : undefined}
                                >
                                    {product.name}
                                </span>
                            ))
                        ) : (
                            <span className={[
                                'inline-flex items-center rounded-full border',
                                'border-card-normal bg-white px-2 py-1',
                                'text-paragraph-xs font-semibold text-text-subtle',
                            ].join(' ')}>
                                &mdash;
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ProjectListSection = ({
    activeEnvironmentTab,
    activeProjects,
    isLoading,
    pagination,
    onPageChange,
    productQualifications,
    profileId,
}: ProjectListSectionProps) => {
    const intl = useIntl();

    const [createTestApiProject, { loading: isCreatingProject }] = useMutation(CREATE_TEST_API_PROJECT);
    const [updateProjectProducts, { loading: isEditingProject }] = useMutation(UPDATE_PROJECT_PRODUCTS);
    const [updateProjectLanguages] = useMutation(UPDATE_PROJECT_LANGUAGES);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState<ProjectCardProject | null>(null);

    const handleCreateTestProject = async (values: {
        projectName: string;
        primaryLanguageCode: string;
        secondaryLanguageCode: string;
        productIds: number[];
    }): Promise<string | undefined> => {
        if (!profileId) return undefined;
        try {
            const { data } = await createTestApiProject({
                variables: {
                    profileId,
                    projectName: values.projectName,
                    languageId1: values.primaryLanguageCode,
                    languageId2: values.secondaryLanguageCode,
                    productIds: values.productIds,
                },
                refetchQueries: [TEST_API_PROJECTS],
            });
            return data?.createTestApiProject ?? 'SERVER_ERROR';
        } catch {
            return 'SERVER_ERROR';
        }
    };

    const handleEditTestProject = async (values: {
        projectId: number;
        productIds: number[];
    }): Promise<string | undefined> => {
        if (!profileId) return undefined;
        try {
            const { data } = await updateProjectProducts({
                variables: {
                    profileId,
                    projectId: values.projectId,
                    productIds: values.productIds,
                },
                refetchQueries: [TEST_API_PROJECTS],
            });
            return data?.updateProjectProducts ?? 'SERVER_ERROR';
        } catch {
            return 'SERVER_ERROR';
        }
    };

    const handleUpdateLanguage = async (values: {
        projectId: number;
        languageId1?: number;
        languageId2?: number;
    }): Promise<string | undefined> => {
        if (!profileId) return undefined;
        try {
            const { data } = await updateProjectLanguages({
                variables: {
                    profileId,
                    projectId: values.projectId,
                    languageId1: values.languageId1,
                    languageId2: values.languageId2,
                },
                refetchQueries: [TEST_API_PROJECTS],
                awaitRefetchQueries: true,
            });
            return data?.updateProjectLanguages ?? 'SERVER_ERROR';
        } catch {
            return 'SERVER_ERROR';
        }
    };

    const handleEditClick = (project: ProjectCardProject) => {
        setEditingProject(project);
        setIsEditModalOpen(true);
    };
    const paginationBtnClass = [
        'rounded-full border border-card-normal px-4 py-2',
        'text-paragraph-xs font-semibold text-text-subtle',
        'transition hover:text-text-default hover:border-card-strong',
        'disabled:cursor-not-allowed disabled:opacity-50',
    ].join(' ');

    return (
        <div className='mt-12 xl:pr-4 2xl:pr-20 3xl:pr-2'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
                <h2 className='text-2xl font-bold'>
                    <FormattedMessage id="LP_DEVELOPER_PROJECTS_TITLE" defaultMessage="Projects" />
                </h2>
                {activeEnvironmentTab === 'test' && (
                    <PrimaryButton
                        onClick={() => setIsCreateModalOpen(true)}
                        type='button'
                        text={intl.formatMessage({
                            id: 'LP_DEVELOPER_CREATE_PROJECT',
                            defaultMessage: 'Create new test project',
                        })}
                        className='px-5 py-2.5 rounded-full text-label-reg font-bold'
                    />
                )}
            </div>
            <div className='mt-2 text-paragraph-reg text-text-subtle'>
                <FormattedMessage
                    id="LP_DEVELOPER_PROJECTS_DESCRIPTION"
                    defaultMessage="List of available projects, showing project setup and enabled products."
                />
            </div>
            <div className='mt-6 grid gap-4 lg:grid-cols-2'>
                {isLoading && (
                    <>
                        {[0, 1].map((index) => (
                            <div
                                key={`project-skeleton-${index}`}
                                className='border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
                            >
                                <div className='flex flex-col gap-y-3'>
                                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={220} />
                                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={16} width={140} />
                                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={18} width={280} />
                                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={16} width={160} />
                                </div>
                            </div>
                        ))}
                    </>
                )}
                {!isLoading && activeProjects.length === 0 && (
                    <div className='text-paragraph-reg text-text-subtle'>
                        <FormattedMessage
                            id="LP_DEVELOPER_PROJECTS_EMPTY"
                            defaultMessage="No projects found for this environment."
                        />
                    </div>
                )}
                {!isLoading && activeProjects.length > 0 && (
                    <>
                        {activeProjects.map((project) => (
                            <ProjectCard
                                intl={intl}
                                key={project.id}
                                project={project}
                                activeEnvironmentTab={activeEnvironmentTab}
                                onEdit={handleEditClick}
                                onUpdateLanguage={handleUpdateLanguage}
                            />
                        ))}
                    </>
                )}
            </div>
            {pagination && onPageChange && (
                <div className='mt-6 flex flex-wrap items-center justify-center gap-3'>
                    <button
                        type='button'
                        className={paginationBtnClass}
                        onClick={() => onPageChange(1)}
                        disabled={pagination.currentPage <= 1 || isLoading}
                    >
                        <FormattedMessage id="LP_PAGINATION_FIRST" defaultMessage="First" />
                    </button>
                    <button
                        type='button'
                        className={paginationBtnClass}
                        onClick={() => onPageChange(pagination.previousPage ?? pagination.currentPage)}
                        disabled={!pagination.previousPage || isLoading}
                    >
                        <FormattedMessage id="LP_PAGINATION_PREVIOUS" defaultMessage="Previous" />
                    </button>
                    <span className='text-paragraph-xs text-text-subtle'>
                        {isLoading
                            ? <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={220} />
                            : <FormattedMessage
                                id="LP_PAGINATION_STATUS"
                                defaultMessage="Page {current} of {total}"
                                values={{ current: pagination.currentPage, total: pagination.maxPage }}
                            /> }
                    </span>
                    <button
                        type='button'
                        className={paginationBtnClass}
                        onClick={() => onPageChange(pagination.nextPage ?? pagination.currentPage)}
                        disabled={!pagination.nextPage || isLoading}
                    >
                        <FormattedMessage id="LP_PAGINATION_NEXT" defaultMessage="Next" />
                    </button>
                    <button
                        type='button'
                        className={paginationBtnClass}
                        onClick={() => onPageChange(pagination.maxPage)}
                        disabled={pagination.currentPage >= pagination.maxPage || isLoading}
                    >
                        <FormattedMessage id="LP_PAGINATION_LAST" defaultMessage="Last" />
                    </button>
                </div>
            )}
            {activeEnvironmentTab === 'test' && (
                <CreateTestProjectModal
                    isOpen={isCreateModalOpen}
                    setOpen={setIsCreateModalOpen}
                    productQualifications={productQualifications}
                    onSubmit={async (values) => {
                        const result = await handleCreateTestProject(values);
                        if (result === 'SUCCESS') {
                            setIsCreateModalOpen(false);
                        }
                        return result;
                    }}
                    isSubmitting={isCreatingProject}
                />
            )}
            {activeEnvironmentTab === 'test' && (
                <EditTestProjectModal
                    isOpen={isEditModalOpen}
                    setOpen={(open) => {
                        setIsEditModalOpen(open);
                        if (!open) setEditingProject(null);
                    }}
                    project={editingProject}
                    productQualifications={productQualifications}
                    onSubmit={async (values) => {
                        const result = await handleEditTestProject(values);
                        if (result === 'SUCCESS') {
                            setIsEditModalOpen(false);
                            setEditingProject(null);
                        }
                        return result;
                    }}
                    isSubmitting={isEditingProject}
                />
            )}
        </div>
    );
};

export default ProjectListSection;
